import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { ChevronDownIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button, DialogContent } from '@mui/material';
import { darken } from 'polished';
import styled, { useTheme } from 'styled-components';

import { TenantEntities } from '@/features/canvas/api/tenant-sso';
import { editViewerGroups, getViewerGroups } from '@/features/publicLinks/api';
import { Dialog } from '@/features/ui/components/Dialog';

const ViewerGroupsDialog = ({
  onClose,
  userId,
  groups,
}: {
  groups: TenantEntities['groups'];
  onClose: () => void;
  userId: number;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();
  const [isListOpen, setIsListOpen] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<TenantEntities['groups']>([]);
  const [groupEntities, setGroupEntities] = useState(groups);
  const listRef = useRef<HTMLDivElement | null>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (listRef.current && !listRef.current.contains(event.target as Node)) {
      setIsListOpen(false);
    }

    const id = (event.target as HTMLElement)?.id;
    const match = id.match(/^add-group#(\d+)$/);

    if (match) {
      const groupId = match[1];
      void handleAddGroup(groupEntities.find(item => item.id === Number(groupId))!);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroups]);

  const { refetch } = useQuery(getViewerGroups.name, () => getViewerGroups(userId ?? 0), {
    onSuccess: (data: TenantEntities['groups']) => {
      setIsOpen(true);
      setSelectedGroups(data);
      setGroupEntities(groups.filter(item => !data.some(x => x.id === item.id)));
    },
  });

  const { mutateAsync } = useMutation(
    ({ id, updateGroups }: { id: number; updateGroups: number[] }) =>
      editViewerGroups(id, updateGroups)
  );

  useEffect(() => {
    if (userId) {
      void refetch();
    }
  }, [userId, refetch]);

  const handleAddGroup = async (item: TenantEntities['groups'][number]) => {
    setSelectedGroups(prev => [...prev, item]);
    setGroupEntities(prev => prev.filter(x => x.id !== item.id));
    await mutateAsync({ id: userId, updateGroups: [...selectedGroups, item].map(x => x.id) });
  };

  const handleRemoveGroup = async (item: TenantEntities['groups'][number]) => {
    setSelectedGroups(prev => prev.filter(x => x.id !== item.id));
    setGroupEntities(prev => [...prev, item]);
    await mutateAsync({
      id: userId,
      updateGroups: selectedGroups.filter(x => x.id !== item.id).map(x => x.id),
    });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        setIsOpen(false);
        onClose();
      }}
    >
      <DialogContent
        sx={{
          color: theme.palette.brand.textPrimary,
          padding: '0 2rem',
          overflow: 'visible',
        }}
      >
        <Title>{t('access.groups')}</Title>
        {selectedGroups.map(item => (
          <SelectedListItemWrapper key={item.id}>
            <SelectedListItem>
              <p>{item.name}</p>
            </SelectedListItem>
            <TrashBinWrapper type="button" onClick={() => handleRemoveGroup(item)}>
              <TrashIcon width={15} height={15} />
            </TrashBinWrapper>
          </SelectedListItemWrapper>
        ))}
        <AddViewerButtonWrapper ref={listRef}>
          <AddViewerButton onClick={() => setIsListOpen(!isListOpen)}>
            <p>{t('access.add_to_group')}</p>
            <ChevronDownIcon width={20} height={20} />
          </AddViewerButton>
          {isListOpen && (
            <ViewerList>
              <ListWrapper>
                {groupEntities.length > 0 ? (
                  groupEntities.map(item => (
                    <ListItem key={item.id} id={`add-group#${item.id}`}>
                      {item.name}
                    </ListItem>
                  ))
                ) : (
                  <ListItem>{t('empty_list')}</ListItem>
                )}
              </ListWrapper>
            </ViewerList>
          )}
        </AddViewerButtonWrapper>
      </DialogContent>
    </Dialog>
  );
};

const Title = styled.h3`
  color: ${({ theme }) => theme.palette.brand.primary};
  font-size: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid ${({ theme }) => theme.palette.brand.backgroundInputFocus};
  margin-bottom: 2rem;
`;

const AddViewerButton = styled(Button)`
  background-color: ${({ theme }) => darken(0.1, theme.palette.brand.backgroundDialog)};
  padding: 0.25rem 1.5rem;
  border-radius: 2rem;
  box-shadow: ${({ theme }) => theme.shadows[0]};
  color: ${({ theme }) => theme.palette.brand.white};
  font-size: 1.5rem;
  font-weight: 600;
  text-transform: capitalize;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${({ theme }) => theme.palette.brand.primary};
  position: relative;

  :hover {
    background-color: ${({ theme }) => darken(0.2, theme.palette.brand.backgroundDialog)};
  }
`;

const AddViewerButtonWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 1rem;
`;

const ViewerList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: max-content;
  max-height: 250px;
  position: absolute;
  left: 0;
  top: calc(100% + 10px);
  z-index: 100;
  border-radius: 10px;
  padding: 10px;
  overflow-y: auto;
  box-shadow: ${({ theme }) => theme.shadows['19']};
  background-color: ${({ theme }) => theme.palette.background.default};
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ListItem = styled(Button)`
  font-size: 1.25rem;
  text-transform: none;
  justify-content: start;
  width: 100%;
  padding: 0.5rem 0.5rem;
  margin: 0.25rem 0;
  color: ${({ theme }) => theme.palette.brand.primary};

  :hover {
    background-color: ${({ theme }) => theme.palette.brand.backgroundDialog};
  }
`;

const SelectedListItem = styled.div`
  font-size: 1.25rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  padding: 0.5rem 0.5rem;
  margin: 0.25rem 0;
  width: 100%;
  color: ${({ theme }) => theme.palette.brand.primary};
`;

const TrashBinWrapper = styled.button`
  color: ${({ theme }) => theme.palette.brand.primary};
  padding: 0.5rem;
  width: max-content;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 300ms ease-in-out;
  border-radius: 5px;

  :hover {
    background-color: ${({ theme }) => theme.palette.brand.backgroundDialog};
  }
`;

const SelectedListItemWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

export default ViewerGroupsDialog;
